import {updateAccessToken, logout} from '../../../session/actions/actions';
import {kc} from '../keycloak';

const BASE_URL = {};
BASE_URL.part = process.env.REP_API_URL;
BASE_URL.auth = process.env.REP_AUTH_URL;
BASE_URL.form = process.env.REP_FORM_URL;

const addTokenIfIsAuthenticated = (authenticated, token, config) => {
  if (!authenticated) {
    return config;
  }
  if (token) {
    return {
      ...config,
      'headers': {
        'Authorization': `Bearer ${token}`,
      },
    };
  }
  throw new Error('NO_TOKEN_SAVED');
};

const addContentType = (config, contentType) => {
  if (!contentType) {
    return config;
  }
  return {
    ...config,
    'headers': {...config.headers, 'Content-Type': contentType},
  };
};

const addBody = (data, configWithHeaders) => {
  if (!data) {
    return configWithHeaders;
  }
  const {headers = {}} = configWithHeaders;
  if (!headers['Content-Type']) {
    return {
      ...configWithHeaders,
      'body': data,
    };
  }
  return {
    ...configWithHeaders,
    'body': JSON.stringify(data),
  };
};

const addHeaders = (config, {authenticated, token}, contentType) => {
  let configWithHeaders = addTokenIfIsAuthenticated(
    authenticated,
    token,
    config
  );
  configWithHeaders = addContentType(configWithHeaders, contentType);
  return configWithHeaders;
};

const updateToken = (next, config) =>
  new Promise((resolve, reject) => {
    console.log('update token');
    kc.updateToken()
      .then(() => {
        next(updateAccessToken(kc.token));
        config.headers.Authorization = `Bearer ${kc.token}`;
        resolve();
      })
      .catch((e) => {
        kc.logout();
        next(logout());
        reject(e);
      });
  });

const callEndpoint = ({microservice, endpoint, config}) =>
  fetch(BASE_URL[microservice] + endpoint, config).then(
    (response) => {
      if (!response.ok) {
        return Promise.reject(response);
      }
      return response;
    },
    (error) => {
      return Promise.reject({
        'ok': false,
        'status': 503,
      });
    }
  );

const refreshToken = async(
  response,
  {next, config, microservice, endpoint}
) => {
  if (response.status !== 401 && response.status !== 403) {
    return Promise.reject(response);
  }
  await updateToken(next, config);
  config.headers.Authorization = `Bearer ${kc.token}`;
  return callEndpoint({microservice, endpoint, config});
};

const parseResponse = async(response) => {
  const json = await response.text();
  return json === '' ? {} : JSON.parse(json);
};

const resolveApiCall = (response) =>
  Promise.resolve(response)
    .then((response) => parseResponse(response))
    .catch((e) => Promise.reject(e.message));

async function callApi({
  endpoint,
  authenticated,
  microservice,
  method,
  data,
  token,
  contentType,
  store,
  next,
}) {
  const configWithHeaders = addHeaders({method}, {authenticated, token}, contentType);
  const config = addBody(data, configWithHeaders);
  const t = kc;
  console.log(t);
  if (kc.isTokenExpired()) {
    await updateToken(next, config);
  }

  const response = await callEndpoint({microservice, endpoint, config})
    .catch((response) => {
      return refreshToken(response, {next, config, microservice, endpoint});
    })
    .catch((error) => error);

  if (!response.ok) {
    const errorConexion = {
      'code': '503',
      'type': 'Error de conexión con el API',
      'status': 503,
    };
    const json =
      response.status === 503 ? errorConexion : await parseResponse(response);
    return Promise.reject({'status': response.status, 'data': json});
  }

  return resolveApiCall(response);
}

const getToken = (authenticated, store) => {
  if (authenticated && store.getState().session) {
    return store.getState().session.token;
  }
  return null;
};

export const CALL_API = 'CALL_API';

const executeActions = (next, {error, response}, types) => {
  const actionTypes = [].concat(types);

  actionTypes.forEach((actionType) => {
    if (typeof actionType === 'function') {
      const {type, ...extras} = actionType(response || error);
      return next({error, response, ...extras, type});
    }

    if (typeof actionType === 'object') {
      return next({error, response, ...actionType});
    }

    return next({
      response,
      error,
      'type': actionType,
    });
  });
};

export default (store) => (next) => (action) => {
  const callAPI = action[CALL_API];

  // So the middleware doesn't get applied to every single action
  if (typeof callAPI === 'undefined') {
    return next(action);
  }

  const {
    endpoint,
    types,
    authenticated,
    method,
    data,
    microservice,
    contentType,
  } = callAPI;

  const [requestType, successTypes, errorTypes] = types;

  next({
    'type': requestType,
  });
  const token = getToken(authenticated, store);

  // Passing the authenticated boolean back in our data will let us distinguish between normal and secret quotes
  return callApi({
    endpoint,
    authenticated,
    microservice,
    method,
    data,
    token,
    contentType,
    store,
    next,
  })
    .then((response) => {
      return executeActions(next, {response}, successTypes);
    })
    .catch((error) => {
      return executeActions(next, {error}, errorTypes);
    });
};
